import React, {
  useContext,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { generateDataAttr } from '../utils/generateDataAttr';
import { st, classes } from './CloseButton.st.css';
import ButtonCore from '../Button/ButtonCore';
import { CloseButtonProps } from './CloseButton.types';
import { ThemeContext } from '../ThemeProvider/ThemeContext';
import { Close, CloseLarge } from '@wix/wix-ui-icons-common/system';
import { SIZES, SKINS } from './CloseButton.constants';
import { ThemeInterface } from '../ThemeProvider';
import { useIcons } from '../WixDesignSystemIconThemeProvider';

const CHILD_SIZE = '18px';

const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  (
    {
      skin = SKINS.standard,
      size = SIZES.small,
      disabled = false,
      autoFocus = false,
      className,
      dataHook,
      children,
      ...rest
    }: CloseButtonProps,
    ref,
  ) => {
    const icons = useIcons('CloseButton', {
      Close,
      CloseLarge,
    });

    const sizeToIconMap = {
      small: <icons.Close data-hook="close" />,
      medium: <icons.CloseLarge data-hook="close-medium" />,
      large: <icons.CloseLarge data-hook="close-large" size="12" />,
    };

    const theme = useContext<ThemeInterface>(ThemeContext);
    const { icons: themeIcons } = theme || {};

    const closeIcon = themeIcons?.CloseButton?.[size] ?? sizeToIconMap[size];

    const buttonRef = useRef<HTMLButtonElement>(null);

    useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement, []);

    useEffect(() => {
      if (autoFocus) {
        buttonRef.current?.focus();
      }
    }, [autoFocus]);

    return (
      <ButtonCore
        ref={buttonRef}
        {...rest}
        disabled={disabled}
        className={st(classes.root, { skin, size }, className)}
        {...generateDataAttr({ skin, size }, ['skin', 'size'])}
        data-hook={dataHook}
      >
        {children
          ? React.cloneElement(children as React.ReactElement, {
              size: CHILD_SIZE,
              width: CHILD_SIZE,
              height: CHILD_SIZE,
            })
          : closeIcon}
      </ButtonCore>
    );
  },
);

CloseButton.displayName = 'CloseButton';

export default CloseButton;
